import React from "react";

const Section = ({ title, children }) => (
  <div className="bg-black border border-[#1E88E5]/10 rounded-2xl p-6 mb-4">
    <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
      <span className="w-2 h-2 rounded-full bg-[#1E88E5]" />
      {title}
    </h3>
    <div className="text-gray-300 text-sm leading-6">{children}</div>
  </div>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-black text-white pt-14 md:pt-16">
      <div className="max-w-3xl mx-auto px-4 py-10 pb-16">
        <header className="mb-6 border-b border-[#1E88E5]/10 pb-4">
          <h1 className="text-3xl font-bold">Privacy Policy</h1>
          <p className="text-gray-400 mt-2 text-sm">Last updated: September 1, 2026</p>
        </header>

        <div className="bg-gradient-to-r from-[#1E88E5]/10 to-transparent border border-[#1E88E5]/20 rounded-2xl p-6 mb-6">
          <p className="text-gray-300 text-sm leading-6 mb-3">
            BetBond provides technology so users can form peer-to-peer sports offers, match with
            counterparties, and settle through escrow on the platform. We charge service fees on
            completed P2P activity. We do not set conditions for users, do not take the other side of
            user agreements, and earn revenue from fees—not from whether a fixture outcome favors one
            party or another.
          </p>
          <p className="text-gray-300 text-sm leading-6">
            This policy summarizes what personal data we collect, why we use it, how we share it,
            and your choices. By using BetBond, you agree to these practices.
          </p>
        </div>

        <Section title="1. Information we collect">
          <p className="font-semibold text-white mb-2">You provide:</p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
            <li>
              Account: username, email, password (stored securely), display name, date of birth
              where required
            </li>
            <li>Profile details you choose (e.g., photo, bio, location)</li>
            <li>Identity verification materials if we request them</li>
            <li>Content you send or post (chat messages and P2P offer details)</li>
          </ul>
          <p className="font-semibold text-white mb-2">Payments and activity:</p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
            <li>
              Payment details through third-party processors (we do not store full card numbers)
            </li>
            <li>
              Records of funding, withdrawals, P2P escrow, settlements, service fees, and in-app balances
            </li>
          </ul>
          <p className="font-semibold text-white mb-2">Automatically:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Usage, device, and log data (e.g., IP address, approximate location from IP, crashes)</li>
            <li>Session timing data (such as session start, last activity, and break status) to apply mandatory session breaks</li>
          </ul>
        </Section>

        <Section title="2. How we use information">
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Run P2P matching: offers, invites, escrow locks, settlements, applying the 7% service fee</li>
            <li>Run community tips: read and share calls on fixtures alongside P2P matching</li>
            <li>Use third-party sports data where needed to verify fixture outcomes for agreed P2P conditions</li>
            <li>Check eligibility (including age where required), verify accounts, and reduce fraud or abuse</li>
            <li>Support you, send operational messages, improve the product, and meet legal obligations</li>
            <li>Apply session breaks and support balanced platform use</li>
          </ul>
        </Section>

        <Section title="3. How we share information">
          <p className="mb-3 font-semibold text-white">
            We do not sell your personal information. We share it only where needed:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>With other users: information you make public on the platform</li>
            <li>
              With processors and vendors: payments, hosting, verification, analytics, outcome or
              event data providers, customer support tools
            </li>
            <li>For legal reasons: to comply with law, respond to lawful requests, or protect rights and safety</li>
          </ul>
        </Section>

        <Section title="4. Security">
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li>
              We use measures such as encryption in transit, secure password handling, access controls,
              and monitoring
            </li>
            <li>You should use a strong password and protect your login</li>
          </ul>
          <p>
            No method of storage or transmission is perfectly secure; we cannot guarantee absolute
            security.
          </p>
        </Section>

        <Section title="5. Your choices and retention">
          <p className="font-semibold text-white mb-2">Deleting your account:</p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
            <li>You can delete your account in the app under Profile → Settings → Delete account</li>
            <li>Deletion is permanent—not the same as logging out or temporarily deactivating an account</li>
            <li>
              You confirm with your password in the app. If no activity is blocking deletion, the
              account is deleted immediately
            </li>
            <li>If activity is blocking deletion, you can join the deletion queue and cancel the queue before deletion completes</li>
            <li>
              We may block deletion until wallet balances are cleared and any open P2P offers,
              invites, or escrow settlements that affect other users are finished
            </li>
            <li>
              When deletion completes, we remove or anonymize profile and user-authored content; some
              transaction and compliance records may be kept as described below
            </li>
          </ul>
          <p className="font-semibold text-white mb-2">Rights:</p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
            <li>
              Request access, correction, or deletion where applicable (some records must be kept for
              law or disputes)
            </li>
            <li>
              Opt out of promotional messages where offered; operational emails may still be required
            </li>
          </ul>
          <p className="font-semibold text-white mb-2">Retention (summary):</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Data needed for an active account while you use the service</li>
            <li>Transaction records often kept longer for compliance (for example, several years)</li>
            <li>We may remove or anonymize data when no longer needed</li>
          </ul>
        </Section>

        <Section title="6. Children, international processing, updates, contact">
          <p className="font-semibold text-white mb-2">Children</p>
          <p className="mb-4">
            BetBond is not for users under 18. If we learn a minor&apos;s data was collected, we will
            take steps to delete it and restrict the account.
          </p>
          <p className="font-semibold text-white mb-2">International processing</p>
          <p className="mb-4">
            We may process or store data in Rwanda and other countries where our vendors operate,
            using appropriate safeguards where required.
          </p>
          <p className="font-semibold text-white mb-2">Changes</p>
          <p className="mb-4">
            We may update this policy and will change the last updated date. For important changes, we
            may notify you in the app or by email.
          </p>
          <p className="font-semibold text-white mb-2">Contact</p>
          <p>
            Questions:{" "}
            <a href="mailto:betbondofficial@gmail.com" className="text-[#1E88E5] hover:underline">
              betbondofficial@gmail.com
            </a>
          </p>
        </Section>
      </div>
    </div>
  );
}
